/**
 * @fileoverview AR-924 — Staged items of the "Upload for class" flow and the pure rules over them.
 * Files are validated client-side when staged; the real upload happens on confirmation, per student.
 * A file can belong to several students (group work): it is sent once to each of them.
 */

import { fileKindOf, matchStudentByFileName, rejectFile } from '../teacherUpload'
import type { FileKind, FileRejectionReason, MatchableStudent } from '../teacherUpload'

export type StagedStatus = 'ready' | 'uploading' | 'uploaded' | 'added' | 'failed'
/** `only`: nobody else could receive it — the activity has a single uploadable student. */
export type MatchedBy = 'name' | 'user' | 'only' | null

export interface StagedItem {
  readonly id: string
  readonly kind: 'file' | 'link'
  readonly name: string
  readonly file?: File
  readonly url?: string
  readonly fileKind: FileKind | 'link'
  readonly sizeInBytes: number
  /** Object URL for image previews (revoked by `releasePreviews`). */
  readonly preview: string | null
  readonly status: StagedStatus
  readonly percent: number
  readonly failReason: FileRejectionReason | 'upload' | null
  /** Every student who receives this item; empty = "needs a student". */
  readonly studentIds: readonly string[]
  /** Students the item was already sent to, so a retry only covers the rest. */
  readonly sentTo: readonly string[]
  readonly matchedBy: MatchedBy
}

let sequence = 0
const nextId = () => `staged-${++sequence}`

/** With a single uploadable student there is nothing to match: every item is theirs. */
export function onlyStudentId(students: readonly MatchableStudent[]): string | null {
  return students.length === 1 ? students[0].id : null
}

export function stageFile(file: File, students: readonly MatchableStudent[]): StagedItem {
  const reason = rejectFile(file)
  const kind = fileKindOf(file.type)
  const isValid = reason === null
  const hasPreview = isValid && kind === 'image'
  const byName = isValid ? matchStudentByFileName(file.name, students) : null
  const studentId = byName ?? (isValid ? onlyStudentId(students) : null)
  return {
    id: nextId(),
    kind: 'file',
    name: file.name,
    file,
    fileKind: kind,
    sizeInBytes: file.size,
    preview: hasPreview ? URL.createObjectURL(file) : null,
    status: isValid ? 'ready' : 'failed',
    percent: 0,
    failReason: reason,
    studentIds: studentId ? [studentId] : [],
    sentTo: [],
    matchedBy: byName ? 'name' : studentId ? 'only' : null,
  }
}

export function stageLink(
  url: string, chosenStudentIds: readonly string[], students: readonly MatchableStudent[] = [],
): StagedItem {
  const only = onlyStudentId(students)
  const studentIds = chosenStudentIds.length > 0 ? chosenStudentIds : only ? [only] : []
  return {
    id: nextId(),
    kind: 'link',
    name: url,
    url,
    fileKind: 'link',
    sizeInBytes: 0,
    preview: null,
    status: 'added',
    percent: 0,
    failReason: null,
    studentIds,
    sentTo: [],
    matchedBy: chosenStudentIds.length > 0 ? 'user' : studentIds.length > 0 ? 'only' : null,
  }
}

export function releasePreviews(items: readonly StagedItem[]): void {
  items.forEach(item => item.preview && URL.revokeObjectURL(item.preview))
}

export const rejectedLocally = (item: StagedItem): boolean => item.status === 'failed' && item.failReason !== 'upload'

/** Upload failures stay (retryable); only local rejections are left out of step 2. */
export const uploadable = (items: readonly StagedItem[]): StagedItem[] => items.filter(item => !rejectedLocally(item))

export const pending = (item: StagedItem): boolean =>
  item.status === 'ready' || item.status === 'added' || (item.status === 'failed' && item.failReason === 'upload')

/** Students of the item still waiting for it (a retry skips the ones already sent). */
export const unsentStudentIds = (item: StagedItem): string[] =>
  item.studentIds.filter(studentId => !item.sentTo.includes(studentId))

export const fullySent = (item: StagedItem): boolean => unsentStudentIds(item).length === 0

/** Whole class: the item goes to every student the teacher can upload for (meaningless with one student). */
export const coversEveryone = (studentIds: readonly string[], everyone: readonly string[]): boolean =>
  everyone.length > 1 && everyone.every(studentId => studentIds.includes(studentId))

export interface FileBatch {
  /** Students still waiting for every file of the batch, in a stable order. */
  readonly studentIds: readonly string[]
  readonly files: readonly StagedItem[]
}

/**
 * Pending files grouped by the exact set of students still waiting for them: one request per batch — the
 * single-student route for one student, `files/bulk` for a group — so a shared file is uploaded once.
 */
export function batchFilesByRecipients(items: readonly StagedItem[]): FileBatch[] {
  const batches = new Map<string, FileBatch>()
  for (const item of items.filter(pending)) {
    if (item.kind !== 'file') continue
    const studentIds = [...unsentStudentIds(item)].sort()
    if (studentIds.length === 0) continue
    const key = studentIds.join('|')
    const batch = batches.get(key) ?? { studentIds, files: [] }
    batches.set(key, { ...batch, files: [...batch.files, item] })
  }
  return [...batches.values()]
}

export interface StagedSummary {
  ready: number
  uploaded: number
  uploading: number
  /** Rejected locally (type/size). */
  failed: number
  /** Sent and refused by the backend — retryable. */
  uploadFailed: number
  firstFailReason: FileRejectionReason | null
}

export function summarize(items: readonly StagedItem[]): StagedSummary {
  const count = (status: StagedStatus) => items.filter(item => item.status === status).length
  const rejected = items.filter(rejectedLocally)
  return {
    ready: count('ready') + count('added'),
    uploaded: count('uploaded'),
    uploading: count('uploading'),
    failed: rejected.length,
    uploadFailed: count('failed') - rejected.length,
    firstFailReason: (rejected[0]?.failReason as FileRejectionReason | undefined) ?? null,
  }
}

/** One group per student who still has something to receive; a shared item appears in every group. */
export function groupByStudent(items: readonly StagedItem[]): Map<string, StagedItem[]> {
  const map = new Map<string, StagedItem[]>()
  for (const item of items.filter(pending)) {
    for (const studentId of unsentStudentIds(item)) {
      map.set(studentId, [...(map.get(studentId) ?? []), item])
    }
  }
  return map
}

export function groupedWith(item: StagedItem, items: readonly StagedItem[]): StagedItem | null {
  if (item.studentIds.length === 0 || item.kind !== 'file') return null
  const earlierItems = items.slice(0, items.indexOf(item))
  const sharesStudent = (other: StagedItem) =>
    other.kind === 'file' && other.studentIds.some(studentId => item.studentIds.includes(studentId))
  return earlierItems.find(sharesStudent) ?? null
}
